import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLang } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Star, ArrowRight, Shield, Coins, Users, ChevronRight, Store, UtensilsCrossed, Globe, HeartHandshake } from 'lucide-react';
import { isBusiness } from '@/lib/roles';

const HERO_IMAGE = "/hero-womio-v5-0.png";
const FOOD_IMAGES = [
  "https://images.unsplash.com/photo-1758520388383-55023490a258?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200",
  "https://images.unsplash.com/photo-1463860914822-61dc3ee606f7?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200",
];

export default function Landing() {
  const { login, user } = useAuth();
  const { t, lang, switchLang } = useLang();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      <nav className="glass-header fixed top-0 left-0 right-0 z-50 px-4 sm:px-6 py-4" data-testid="landing-navbar">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <img src="/logo-wordmark@2x.png" alt="Womio" className="h-7 w-auto" data-testid="landing-footer-logo" />
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Language Switch */}
            <button onClick={() => switchLang(lang === 'it' ? 'en' : 'it')} className="p-2 rounded-full hover:bg-white/20 transition-colors text-muted-foreground" data-testid="landing-lang-switch">
              <Globe className="w-4 h-4" strokeWidth={1.5} />
            </button>
            {user ? (
              <Button data-testid="go-dashboard-btn" onClick={() => navigate(isBusiness(user) ? '/restaurant/dashboard' : '/dashboard')} className="rounded-full btn-lift text-xs sm:text-sm">
                {t('go_dashboard')} <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <>
                <Button data-testid="landing-user-login-btn" onClick={() => login('user')} variant="ghost" className="rounded-full btn-lift text-xs sm:text-sm">
                  {t('landing_customer_login')}
                </Button>
                <Button data-testid="landing-restaurant-login-btn" onClick={() => navigate('/restaurant-auth')} variant="outline" className="rounded-full btn-lift text-xs sm:text-sm">
                  <Store className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> {t('landing_business_login')}
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative min-h-[80vh] sm:min-h-[85vh] flex items-end" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_IMAGE} alt="Restaurant" className="w-full h-full object-cover" loading="eager" />
          <div className="hero-overlay absolute inset-0" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pb-12 sm:pb-20 w-full">
          <div className="max-w-2xl opacity-0 animate-fade-in-up">
            <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 mb-4 sm:mb-6 text-xs sm:text-sm px-3 py-1">{t('landing_badge')}</Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white leading-tight mb-4 sm:mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", textShadow: '0 2px 20px rgba(0,0,0,0.3)' }}>
              {t('landing_h1_1')}<br />{t('landing_h1_2')}
            </h1>
            <p className="text-base sm:text-lg text-white/90 mb-6 sm:mb-8 max-w-lg font-sans leading-relaxed">{t('landing_sub')}</p>
            <div className="flex gap-3 flex-wrap">
              <Button data-testid="hero-get-started-btn" size="lg" onClick={user ? () => navigate('/restaurants') : () => navigate('/user-auth?tab=register')} className="rounded-full text-sm sm:text-base px-6 sm:px-8 btn-lift">
                {user ? t('landing_cta') : t('landing_signup_free')} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button data-testid="hero-browse-btn" variant="outline" size="lg" onClick={() => navigate('/restaurants')} className="rounded-full text-sm sm:text-base px-6 sm:px-8 bg-white/10 text-white border-white/30 hover:bg-white/20 hover:text-white btn-lift">
                {t('landing_browse')}
              </Button>
            </div>
            {!user && (
              <button data-testid="hero-business-signup-link" onClick={() => navigate('/restaurant-auth')} className="mt-4 text-sm text-white/90 hover:text-white underline-offset-4 hover:underline font-sans inline-flex items-center gap-1.5 transition-colors">
                <Store className="w-4 h-4" strokeWidth={1.5} /> {t('landing_business_signup')} <ArrowRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 sm:py-24 px-4 sm:px-6" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-16 opacity-0 animate-fade-in-up stagger-1">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-3 font-sans">{t('landing_how_sub')}</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{t('landing_how')}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-5">
            {[
              { icon: <UtensilsCrossed className="w-6 h-6" strokeWidth={1.5} />, title: t('landing_step1_t'), desc: t('landing_step1_d') },
              { icon: <Users className="w-6 h-6" strokeWidth={1.5} />, title: t('landing_step2_t'), desc: t('landing_step2_d') },
              { icon: <Shield className="w-6 h-6" strokeWidth={1.5} />, title: t('landing_step3_t'), desc: t('landing_step3_d') },
              { icon: <Coins className="w-6 h-6" strokeWidth={1.5} />, title: t('landing_step4_t'), desc: t('landing_step4_d') },
              { icon: <HeartHandshake className="w-6 h-6" strokeWidth={1.5} />, title: t('landing_step5_t'), desc: t('landing_step5_d') },
            ].map((step, i) => (
              <div key={step.title} className={`border border-border bg-card rounded-2xl p-6 sm:p-8 card-hover opacity-0 animate-fade-in-up stagger-${i+1}`}>
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary mb-5">{step.icon}</div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{step.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed font-sans">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bento */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 bg-secondary/30">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          <div className="relative rounded-2xl overflow-hidden h-52 sm:h-80" data-testid="landing-customers-box">
            <img src={FOOD_IMAGES[0]} alt="Clienti" className="w-full h-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-0 p-6 sm:p-8">
              <h3 className="text-xl sm:text-2xl text-white font-bold mb-1 sm:mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{t('landing_for_diners')}</h3>
              <p className="text-white/80 text-xs sm:text-sm font-sans">{t('landing_for_diners_d')}</p>
            </div>
          </div>
          <div className="relative rounded-2xl overflow-hidden h-52 sm:h-80" data-testid="landing-businesses-box">
            <img src={FOOD_IMAGES[1]} alt="Attività" className="w-full h-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-0 p-6 sm:p-8">
              <h3 className="text-xl sm:text-2xl text-white font-bold mb-1 sm:mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{t('landing_for_restaurants')}</h3>
              <p className="text-white/80 text-xs sm:text-sm font-sans">{t('landing_for_restaurants_d')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Action 2 Smile - Impegno sociale */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 border-t border-border" data-testid="action2smile-section">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-3 font-sans">{t('a2s_eyebrow')}</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{t('a2s_title')}</h2>
          <p className="text-muted-foreground text-sm sm:text-base font-sans leading-relaxed max-w-2xl mx-auto mb-6">{t('a2s_body')}</p>
          <a href="https://www.action2smile.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-primary font-sans text-sm font-semibold hover:underline" data-testid="action2smile-link">
            {t('a2s_cta')} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </a>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{t('landing_cta2')}</h2>
          <p className="text-muted-foreground mb-6 sm:mb-8 font-sans text-sm sm:text-base">{t('landing_cta2_sub')}</p>
          <Button data-testid="cta-sign-up-btn" size="lg" onClick={user ? () => navigate('/dashboard') : () => navigate('/user-auth?tab=register')} className="rounded-full text-sm sm:text-base px-8 sm:px-10 btn-lift">
            {user ? t('go_dashboard') : t('landing_signup_free')} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      <footer className="border-t border-border py-6 sm:py-8 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2 text-xs sm:text-sm text-muted-foreground font-sans">
          <p>Womio - Word of Mouth, Italian Style</p>
          <p>Performance-based. Receipt-verified.</p>
        </div>
      </footer>
    </div>
  );
}
